/**
 * Button Block for Editor.js
 * Creates email-safe button elements
 */

import { BlockTool } from '@editorjs/editorjs';

export interface ButtonBlockData {
  text: string;
  url: string;
  alignment: 'left' | 'center' | 'right';
  style: 'primary' | 'secondary' | 'dark';
}

export default class ButtonBlock implements BlockTool {
  private data: ButtonBlockData;
  private wrapper: HTMLElement | null = null;

  static get toolbox() {
    return {
      title: 'Button',
      icon: '<svg width="17" height="15" viewBox="0 0 17 15" xmlns="http://www.w3.org/2000/svg"><path d="M1.5 1.5h14v12h-14v-12z" stroke="currentColor" stroke-width="1.5" fill="none" rx="2"/><path d="M4 7.5h9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
    };
  }

  constructor({ data }: { data: ButtonBlockData }) {
    this.data = {
      text: data.text || 'Click Here',
      url: data.url || 'https://www.ejswineshop.com',
      alignment: data.alignment || 'center',
      style: data.style || 'primary',
    };
  }

  render(): HTMLElement {
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add('email-button-block');
    this.wrapper.style.cssText = `
      padding: 16px;
      text-align: ${this.data.alignment};
    `;

    const button = document.createElement('a');
    button.href = this.data.url;
    button.textContent = this.data.text;
    button.contentEditable = 'true';
    button.style.cssText = this.getButtonStyle();

    button.addEventListener('input', () => {
      this.data.text = button.textContent || 'Click Here';
    });

    // Prevent link navigation when editing
    button.addEventListener('click', (e) => {
      e.preventDefault();
    });

    this.wrapper.appendChild(button);
    return this.wrapper;
  }

  private getButtonStyle(): string {
    const baseStyle = `
      display: inline-block;
      padding: 12px 32px;
      font-size: 16px;
      font-weight: 600;
      text-decoration: none;
      border-radius: 4px;
      cursor: text;
      outline: none;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;

    const styles: Record<string, string> = {
      primary: `${baseStyle} background-color: #D4A657; color: #ffffff;`,
      secondary: `${baseStyle} background-color: #7B9D9D; color: #ffffff;`,
      dark: `${baseStyle} background-color: #2b2b2b; color: #ffffff;`,
    };

    return styles[this.data.style] || styles.primary;
  }

  save(): ButtonBlockData {
    return this.data;
  }

  renderSettings(): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.style.padding = '8px';

    // URL input
    const urlLabel = document.createElement('label');
    urlLabel.textContent = 'Button URL:';
    urlLabel.style.cssText = 'display: block; margin-bottom: 4px; font-size: 12px; color: #666;';

    const urlInput = document.createElement('input');
    urlInput.type = 'url';
    urlInput.value = this.data.url;
    urlInput.placeholder = 'https://...';
    urlInput.style.cssText = 'width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px; margin-bottom: 12px;';
    urlInput.addEventListener('change', () => {
      this.data.url = urlInput.value;
    });

    // Style selector
    const styleLabel = document.createElement('label');
    styleLabel.textContent = 'Button Style:';
    styleLabel.style.cssText = 'display: block; margin-bottom: 4px; font-size: 12px; color: #666;';

    const styleSelect = document.createElement('select');
    styleSelect.style.cssText = 'width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px; margin-bottom: 12px;';

    ['primary', 'secondary', 'dark'].forEach((style) => {
      const option = document.createElement('option');
      option.value = style;
      option.textContent = style.charAt(0).toUpperCase() + style.slice(1);
      option.selected = this.data.style === style;
      styleSelect.appendChild(option);
    });

    styleSelect.addEventListener('change', () => {
      this.data.style = styleSelect.value as 'primary' | 'secondary' | 'dark';
      const button = this.wrapper?.querySelector('a');
      if (button) {
        button.style.cssText = this.getButtonStyle();
      }
    });

    // Alignment selector
    const alignLabel = document.createElement('label');
    alignLabel.textContent = 'Alignment:';
    alignLabel.style.cssText = 'display: block; margin-bottom: 4px; font-size: 12px; color: #666;';

    const alignSelect = document.createElement('select');
    alignSelect.style.cssText = 'width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px;';

    ['left', 'center', 'right'].forEach((align) => {
      const option = document.createElement('option');
      option.value = align;
      option.textContent = align.charAt(0).toUpperCase() + align.slice(1);
      option.selected = this.data.alignment === align;
      alignSelect.appendChild(option);
    });

    alignSelect.addEventListener('change', () => {
      this.data.alignment = alignSelect.value as 'left' | 'center' | 'right';
      if (this.wrapper) {
        this.wrapper.style.textAlign = this.data.alignment;
      }
    });

    wrapper.appendChild(urlLabel);
    wrapper.appendChild(urlInput);
    wrapper.appendChild(styleLabel);
    wrapper.appendChild(styleSelect);
    wrapper.appendChild(alignLabel);
    wrapper.appendChild(alignSelect);

    return wrapper;
  }
}
